import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@sea-snag/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Globe,
  Layers,
  MoreHorizontal,
  Ship,
  X,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { AddToCompSetItems } from '../../components/add-to-comp-set-menu';
import { BoatImageGallery } from '../../components/boat-image-gallery';
import { BoatTypeBadge } from '../../components/boat-type-badge';
import { EquipmentScoreBadge } from '../../components/equipment-score-badge';
import { LoadingState } from '../../components/loading-state';
import { LocationCascadeSingle } from '../../components/location-cascade-single';
import { MarketBoatDetailSheet } from '../../components/market-boat-detail-sheet';
import { ActiveFilterBadges, SearchableMultiSelect } from '../../components/market-filter-panel';
import { SortableTableHead } from '../../components/sortable-table-head';
import { WizardFilterPanel } from '../../components/wizard-filter-panel';
import { type MarketSearchParams, useMarketFilters } from '../../hooks/use-market-filters';
import { useRequiredOrganization } from '../../hooks/use-required-organization';
import {
  type FilterOptions,
  type LocationHierarchy,
  type MarketBoat,
  marketDataApi,
} from '../../lib/api-client';
import { formatLengthFt } from '../../lib/format';
import { queryKeys, STALE_TIMES } from '../../lib/query-keys';
import { formatRelativeTime } from '../../lib/time';

const marketSearchSchema = z.object({
  tab: z.enum(['browse', 'tracked']).optional(),
  search: z.string().optional(),
  kind: z.string().optional(),
  models: z.string().optional(),
  homeBase: z.string().optional(),
  charterNames: z.string().optional(),
  minLength: z.coerce.number().optional(),
  maxLength: z.coerce.number().optional(),
  minCabins: z.coerce.number().int().optional(),
  maxCabins: z.coerce.number().int().optional(),
  minBerths: z.coerce.number().int().optional(),
  maxBerths: z.coerce.number().int().optional(),
  minYear: z.coerce.number().int().optional(),
  maxYear: z.coerce.number().int().optional(),
  minWc: z.coerce.number().int().optional(),
  minPeople: z.coerce.number().int().optional(),
  minBeam: z.coerce.number().optional(),
  maxBeam: z.coerce.number().optional(),
  minDraught: z.coerce.number().optional(),
  maxDraught: z.coerce.number().optional(),
  equipmentIds: z.string().optional(),
  countryPid: z.string().uuid().optional(),
  sailingAreaPids: z.string().optional(),
  basePids: z.string().optional(),
  page: z.coerce.number().int().positive().optional(),
  sortBy: z
    .enum(['name', 'kind', 'year', 'lengthM', 'cabins', 'createdAt', 'equipmentScore'])
    .optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

export const Route = createFileRoute('/$orgPid/market-explorer')({
  component: MarketExplorerPage,
  validateSearch: marketSearchSchema,
});

function MarketExplorerPage() {
  const { organization, isLoading } = useRequiredOrganization();

  if (isLoading) return <LoadingState />;

  return <MarketExplorerContent orgPid={organization.pid} />;
}

function MarketExplorerContent({ orgPid }: { orgPid: string }) {
  const queryClient = useQueryClient();
  const searchParams = useSearch({ from: '/$orgPid/market-explorer' }) as MarketSearchParams;
  const marketFilters = useMarketFilters(searchParams);

  const { data: stats } = useQuery({
    queryKey: queryKeys.market.stats(orgPid),
    queryFn: () => marketDataApi.stats(),
    staleTime: STALE_TIMES.MEDIUM,
  });

  const { data: filterOptions } = useQuery({
    queryKey: queryKeys.market.filterOptions(orgPid),
    queryFn: () => marketDataApi.filterOptions(),
    staleTime: STALE_TIMES.MEDIUM,
  });

  const { data: modelOptions } = useQuery({
    queryKey: queryKeys.market.models(orgPid, marketFilters.filters.kind),
    queryFn: () => marketDataApi.models(marketFilters.filters.kind),
    enabled: !!marketFilters.filters.kind,
    staleTime: STALE_TIMES.MEDIUM,
  });

  // Cascading Country → Sailing Area → Base filter data (issue #95).
  const { data: locationHierarchy } = useQuery({
    queryKey: queryKeys.marketData.locationHierarchy(orgPid),
    queryFn: () => marketDataApi.locationHierarchy(),
    staleTime: STALE_TIMES.LONG,
  });

  const trackMutation = useMutation({
    mutationFn: (externalIds: string[]) => marketDataApi.track(externalIds),
    onSuccess: (data) => {
      toast.success(data.length > 1 ? `${data.length} boats tracked` : 'Boat tracked');
      void queryClient.invalidateQueries({ queryKey: queryKeys.market.browseAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.market.stats(orgPid) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.market.trackedAll(orgPid) });
    },
    onError: () => toast.error('Failed to track boat'),
  });

  const untrackMutation = useMutation({
    mutationFn: (externalId: string) => marketDataApi.untrack(externalId),
    onSuccess: () => {
      toast.success('Boat untracked');
      void queryClient.invalidateQueries({ queryKey: queryKeys.market.browseAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.market.stats(orgPid) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.market.trackedAll(orgPid) });
    },
    onError: () => toast.error('Failed to untrack boat'),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Market Explorer</h1>
        <p className="text-muted-foreground">Browse the MMK marketplace and track competitors</p>
      </div>

      {/* Tabs */}
      <Tabs
        value={marketFilters.tab}
        onValueChange={(v) => marketFilters.setTab(v as 'browse' | 'tracked')}
      >
        <TabsList>
          <TabsTrigger value="browse">
            <Globe className="mr-1 size-4" />
            Browse Marketplace
          </TabsTrigger>
          <TabsTrigger value="tracked">
            <Eye className="mr-1 size-4" />
            Tracked ({stats?.trackedBoats ?? 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="browse">
          <BrowseTab
            orgPid={orgPid}
            trackMutation={trackMutation}
            untrackMutation={untrackMutation}
            marketFilters={marketFilters}
            filterOptions={filterOptions}
            modelOptions={modelOptions}
            locationHierarchy={locationHierarchy}
          />
        </TabsContent>

        <TabsContent value="tracked">
          <TrackedTab
            orgPid={orgPid}
            untrackMutation={untrackMutation}
            marketFilters={marketFilters}
            filterOptions={filterOptions}
            modelOptions={modelOptions}
            locationHierarchy={locationHierarchy}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Browse Tab ──

function BrowseTab({
  orgPid,
  trackMutation,
  untrackMutation,
  marketFilters,
  filterOptions,
  modelOptions,
  locationHierarchy,
}: {
  orgPid: string;
  trackMutation: ReturnType<typeof useMutation<MarketBoat[], Error, string[]>>;
  untrackMutation: ReturnType<typeof useMutation<{ message: string }, Error, string>>;
  marketFilters: ReturnType<typeof useMarketFilters>;
  filterOptions?: FilterOptions;
  modelOptions?: { name: string; count: number }[];
  locationHierarchy?: LocationHierarchy;
}) {
  const {
    filters,
    browsePage: page,
    setBrowsePage: setPage,
    setFilter,
    resetFilters,
    activeFilterCount,
    toBrowseParams,
    sort,
    toggleSort,
  } = marketFilters;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedBoat, setSelectedBoat] = useState<MarketBoat | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const browseParams = toBrowseParams();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.market.browse(orgPid, browseParams),
    queryFn: () => marketDataApi.browse(browseParams),
  });

  const boats = data?.data ?? [];
  const meta = data?.meta;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="size-5" />
          Marketplace Boats
        </CardTitle>
        <CardDescription>
          {meta ? `${meta.total.toLocaleString()} boats found` : 'Loading...'}
          {" — click Track to monitor a competitor's pricing"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters — same shape as the Comp Set Wizard (issue: market-explorer-filter)
            so charterers see one consistent filter UX: From/To number inputs,
            Length in ft, merged "Boat Characteristics" section, no Beam/Draught. */}
        {activeFilterCount > 0 && (
          <ActiveFilterBadges
            filters={filters}
            setFilter={setFilter}
            resetFilters={resetFilters}
            filterOptions={filterOptions}
          />
        )}

        {/* Render Location card unconditionally so the Charter Company filter
            is always usable; only the country/sailing-area/base cascade is
            gated on the hierarchy query loading. */}
        <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Location
          </h4>
          {locationHierarchy ? (
            <LocationCascadeSingle
              countries={locationHierarchy}
              countryPid={filters.countryPid}
              sailingAreaPids={filters.sailingAreaPids}
              basePids={filters.basePids}
              onCountryChange={(pid) => setFilter('countryPid', pid)}
              onSailingAreaPidsChange={(pids) => setFilter('sailingAreaPids', pids)}
              onBasePidsChange={(pids) => setFilter('basePids', pids)}
            />
          ) : (
            <p className="text-xs text-muted-foreground">Loading countries…</p>
          )}
          <SearchableMultiSelect
            label="Charter Company"
            selected={filters.charterNames}
            onChange={(names) => setFilter('charterNames', names)}
            options={filterOptions?.charterNames ?? []}
            placeholder="Any company"
          />
        </div>

        <WizardFilterPanel
          filters={filters}
          setFilter={setFilter}
          filterOptions={filterOptions}
          modelOptions={modelOptions}
        />

        {/* Selection toolbar */}
        {selectedIds.size > 0 && (
          <SelectionToolbar
            count={selectedIds.size}
            externalIds={[...selectedIds]}
            onClear={clearSelection}
          />
        )}

        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={
                      boats.length > 0 && boats.every((b) => selectedIds.has(b.externalId))
                        ? true
                        : boats.some((b) => selectedIds.has(b.externalId))
                          ? 'indeterminate'
                          : false
                    }
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedIds(new Set(boats.map((b) => b.externalId)));
                      } else {
                        clearSelection();
                      }
                    }}
                  />
                </TableHead>
                <SortableTableHead column="name" sort={sort} onSort={toggleSort}>
                  Name
                </SortableTableHead>
                <TableHead className="w-10"></TableHead>
                <SortableTableHead column="kind" sort={sort} onSort={toggleSort}>
                  Type
                </SortableTableHead>
                <TableHead>Model</TableHead>
                <SortableTableHead column="lengthM" sort={sort} onSort={toggleSort}>
                  Length (ft)
                </SortableTableHead>
                <SortableTableHead column="cabins" sort={sort} onSort={toggleSort}>
                  Cabins
                </SortableTableHead>
                <SortableTableHead column="year" sort={sort} onSort={toggleSort}>
                  Year
                </SortableTableHead>
                <SortableTableHead column="equipmentScore" sort={sort} onSort={toggleSort}>
                  Equipment
                </SortableTableHead>
                <TableHead>Home Base</TableHead>
                <TableHead>Charter Co.</TableHead>
                <TableHead className="w-[80px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={12} className="h-24 text-center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : boats.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} className="h-24 text-center">
                    <div className="text-muted-foreground">
                      <Globe className="mx-auto mb-2 size-8 opacity-50" />
                      <p>No marketplace boats found.</p>
                      <p className="text-xs">
                        Ensure you have an active MMK integration, or adjust your filters.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                boats.map((boat) => (
                  <TableRow
                    key={boat.externalId}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      setSelectedBoat(boat);
                      setSheetOpen(true);
                    }}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(boat.externalId)}
                        onCheckedChange={() => toggleSelected(boat.externalId)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Ship className="text-muted-foreground size-4 shrink-0" />
                        <div>
                          <div className="flex items-center gap-1.5">
                            <p className="font-medium">{boat.name}</p>
                            {boat.isTracked && (
                              <span
                                className="size-2 rounded-full bg-green-500"
                                title="Tracked"
                                role="status"
                                aria-label="Tracked"
                              />
                            )}
                          </div>
                          {boat.model && (
                            <p className="text-muted-foreground text-xs">{boat.model}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                      <BoatImageGallery
                        boatPid={boat.id}
                        boatName={boat.name}
                        imageCount={boat.imageCount}
                      />
                    </TableCell>
                    <TableCell>
                      <BoatTypeBadge kind={boat.kind} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">{boat.model ?? '—'}</TableCell>
                    <TableCell>{formatLengthFt(boat.lengthM)}</TableCell>
                    <TableCell>{boat.cabins ?? '—'}</TableCell>
                    <TableCell>{boat.year ?? '—'}</TableCell>
                    <TableCell>
                      <EquipmentScoreBadge boat={boat} />
                    </TableCell>
                    <TableCell>
                      <span className="max-w-[150px] truncate" title={boat.homeBase ?? undefined}>
                        {boat.homeBase ?? '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span
                        className="max-w-[150px] truncate"
                        title={boat.charterName ?? undefined}
                      >
                        {boat.charterName ?? '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[200px]">
                          <DropdownMenuItem
                            onClick={() => {
                              if (boat.isTracked) {
                                untrackMutation.mutate(boat.externalId);
                              } else {
                                trackMutation.mutate([boat.externalId]);
                              }
                            }}
                            disabled={trackMutation.isPending || untrackMutation.isPending}
                          >
                            {boat.isTracked ? (
                              <>
                                <EyeOff className="size-4" />
                                Untrack
                              </>
                            ) : (
                              <>
                                <Eye className="size-4" />
                                Track
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <AddToCompSetItems externalIds={[boat.externalId]} />
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground text-sm">
              Page {meta.page} of {meta.totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                <ChevronLeft className="mr-1 size-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= meta.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
                <ChevronRight className="ml-1 size-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      <MarketBoatDetailSheet boat={selectedBoat} open={sheetOpen} onOpenChange={setSheetOpen} />
    </Card>
  );
}

// ── Tracked Tab ──

function TrackedTab({
  orgPid,
  untrackMutation,
  marketFilters,
  filterOptions,
  modelOptions,
  locationHierarchy,
}: {
  orgPid: string;
  untrackMutation: ReturnType<typeof useMutation<{ message: string }, Error, string>>;
  marketFilters: ReturnType<typeof useMarketFilters>;
  filterOptions?: FilterOptions;
  modelOptions?: { name: string; count: number }[];
  locationHierarchy?: LocationHierarchy;
}) {
  const {
    filters,
    trackedPage: page,
    setTrackedPage: setPage,
    setFilter,
    resetFilters,
    activeFilterCount,
    toTrackedParams,
    sort,
    toggleSort,
  } = marketFilters;
  const queryClient = useQueryClient();
  const [selectedBoat, setSelectedBoat] = useState<MarketBoat | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const trackedParams = toTrackedParams();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.market.tracked(orgPid, trackedParams),
    queryFn: () => marketDataApi.tracked(trackedParams),
  });

  const handleUntrack = (boat: MarketBoat) => {
    if (!boat.externalId) return;
    untrackMutation.mutate(boat.externalId, {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.market.trackedAll(orgPid) });
      },
    });
  };

  const boats = data?.data ?? [];
  const meta = data?.meta;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="size-5" />
            Tracked Competitors
          </CardTitle>
          <CardDescription>
            {meta ? `${meta.total.toLocaleString()} tracked boats` : 'Loading...'}
            {' — click a row to view pricing details'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters — see Browse tab comment above. */}
          {activeFilterCount > 0 && (
            <ActiveFilterBadges
              filters={filters}
              setFilter={setFilter}
              resetFilters={resetFilters}
              filterOptions={filterOptions}
            />
          )}

          {/* See Browse tab comment. */}
          <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Location
            </h4>
            {locationHierarchy ? (
              <LocationCascadeSingle
                countries={locationHierarchy}
                countryPid={filters.countryPid}
                sailingAreaPids={filters.sailingAreaPids}
                basePids={filters.basePids}
                onCountryChange={(pid) => setFilter('countryPid', pid)}
                onSailingAreaPidsChange={(pids) => setFilter('sailingAreaPids', pids)}
                onBasePidsChange={(pids) => setFilter('basePids', pids)}
              />
            ) : (
              <p className="text-xs text-muted-foreground">Loading countries…</p>
            )}
            <SearchableMultiSelect
              label="Charter Company"
              selected={filters.charterNames}
              onChange={(names) => setFilter('charterNames', names)}
              options={filterOptions?.charterNames ?? []}
              placeholder="Any company"
            />
          </div>

          <WizardFilterPanel
            filters={filters}
            setFilter={setFilter}
            filterOptions={filterOptions}
            modelOptions={modelOptions}
          />

          {/* Selection toolbar */}
          {selectedIds.size > 0 && (
            <SelectionToolbar
              count={selectedIds.size}
              marketBoatPids={[...selectedIds]}
              onClear={clearSelection}
            />
          )}

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={
                        boats.length > 0 && boats.every((b) => selectedIds.has(b.id))
                          ? true
                          : boats.some((b) => selectedIds.has(b.id))
                            ? 'indeterminate'
                            : false
                      }
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedIds(new Set(boats.map((b) => b.id)));
                        } else {
                          clearSelection();
                        }
                      }}
                    />
                  </TableHead>
                  <SortableTableHead column="name" sort={sort} onSort={toggleSort}>
                    Name
                  </SortableTableHead>
                  <TableHead className="w-10"></TableHead>
                  <SortableTableHead column="kind" sort={sort} onSort={toggleSort}>
                    Type
                  </SortableTableHead>
                  <TableHead>Model</TableHead>
                  <SortableTableHead column="lengthM" sort={sort} onSort={toggleSort}>
                    Length (ft)
                  </SortableTableHead>
                  <SortableTableHead column="cabins" sort={sort} onSort={toggleSort}>
                    Cabins
                  </SortableTableHead>
                  <SortableTableHead column="year" sort={sort} onSort={toggleSort}>
                    Year
                  </SortableTableHead>
                  <SortableTableHead column="equipmentScore" sort={sort} onSort={toggleSort}>
                    Equipment
                  </SortableTableHead>
                  <TableHead>Home Base</TableHead>
                  <TableHead>Charter Co.</TableHead>
                  <TableHead>Price Range</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead className="w-[80px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={14} className="h-24 text-center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : boats.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} className="h-24 text-center">
                      <div className="text-muted-foreground">
                        <Ship className="mx-auto mb-2 size-8 opacity-50" />
                        <p>No boats tracked yet.</p>
                        <p className="text-xs">
                          Switch to the Browse tab to find and track competitors.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  boats.map((boat) => (
                    <TableRow
                      key={boat.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => {
                        setSelectedBoat(boat);
                        setSheetOpen(true);
                      }}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(boat.id)}
                          onCheckedChange={() => toggleSelected(boat.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Ship className="text-muted-foreground size-4 shrink-0" />
                          <div>
                            <p className="font-medium">{boat.name}</p>
                            {boat.model && (
                              <p className="text-muted-foreground text-xs">{boat.model}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                        <BoatImageGallery
                          boatPid={boat.id}
                          boatName={boat.name}
                          imageCount={boat.imageCount}
                        />
                      </TableCell>
                      <TableCell>
                        <BoatTypeBadge kind={boat.kind} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">{boat.model ?? '—'}</TableCell>
                      <TableCell>{formatLengthFt(boat.lengthM)}</TableCell>
                      <TableCell>{boat.cabins ?? '—'}</TableCell>
                      <TableCell>{boat.year ?? '—'}</TableCell>
                      <TableCell>
                        <EquipmentScoreBadge boat={boat} />
                      </TableCell>
                      <TableCell>
                        <span className="max-w-[150px] truncate" title={boat.homeBase ?? undefined}>
                          {boat.homeBase ?? '—'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className="max-w-[150px] truncate"
                          title={boat.charterName ?? undefined}
                        >
                          {boat.charterName ?? '—'}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs tabular-nums">
                        <PriceRange
                          min={boat.priceMin}
                          max={boat.priceMax}
                          currency={boat.priceCurrency}
                        />
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {formatRelativeTime(new Date(boat.lastSeenAt))}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-8"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="min-w-[200px]">
                            <AddToCompSetItems marketBoatPids={[boat.id]} />
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              variant="destructive"
                              onClick={() => handleUntrack(boat)}
                              disabled={untrackMutation.isPending}
                            >
                              <EyeOff className="size-4" />
                              Untrack
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                Page {meta.page} of {meta.totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="mr-1 size-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= meta.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                  <ChevronRight className="ml-1 size-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <MarketBoatDetailSheet boat={selectedBoat} open={sheetOpen} onOpenChange={setSheetOpen} />
    </>
  );
}

// ── Selection Toolbar ──

function SelectionToolbar({
  count,
  marketBoatPids,
  externalIds,
  onClear,
}: {
  count: number;
  marketBoatPids?: string[];
  externalIds?: string[];
  onClear: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-muted/50 px-4 py-2">
      <span className="text-sm font-medium">
        {count} {count === 1 ? 'boat' : 'boats'} selected
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Layers className="mr-1 size-3.5" />
            Add to Competitor Set
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[200px]">
          <AddToCompSetItems
            marketBoatPids={marketBoatPids}
            externalIds={externalIds}
            onSuccess={onClear}
          />
        </DropdownMenuContent>
      </DropdownMenu>
      <Button variant="ghost" size="sm" onClick={onClear}>
        <X className="mr-1 size-3.5" />
        Clear
      </Button>
    </div>
  );
}

function PriceRange({
  min,
  max,
  currency,
}: {
  min: string | null;
  max: string | null;
  currency: string | null;
}) {
  const fmt = useMemo(
    () =>
      new Intl.NumberFormat('en', {
        style: 'currency',
        currency: currency ?? 'EUR',
        maximumFractionDigits: 0,
      }),
    [currency]
  );
  if (!min || !max) return <span className="text-muted-foreground">—</span>;
  const minVal = Number(min);
  const maxVal = Number(max);
  if (minVal === maxVal) return <span>{fmt.format(minVal)}</span>;
  return (
    <span>
      {fmt.format(minVal)}–{fmt.format(maxVal)}
    </span>
  );
}
